import React, { useState, useEffect } from 'react';
import { removeHelper } from '../../utils/tripPlanUtils';
import HelperGirlProfiles from './HelperGirlProfiles';
import HelperDisplayName from './HelperDisplayName';

const HelperManagementModal = ({ isOpen, onClose, tripPlan }) => {
  const [helpers, setHelpers] = useState([]);
  const [removingHelper, setRemovingHelper] = useState(null);

  useEffect(() => {
    if (tripPlan && tripPlan.helpers) {
      setHelpers(tripPlan.helpers);
    }
  }, [tripPlan]);

  const handleRemoveHelper = async (helperEmail) => {
    if (!window.confirm(`คุณต้องการลบ ${helperEmail} ออกจากผู้ช่วยหรือไม่?`)) {
      return;
    }

    setRemovingHelper(helperEmail);
    try {
      await removeHelper(tripPlan.id, helperEmail);
      setHelpers(prev => prev.filter(helper => helper.email !== helperEmail));
      alert('ลบผู้ช่วยสำเร็จ!');
    } catch (error) {
      console.error('Error removing helper:', error);
      alert('เกิดข้อผิดพลาดในการลบผู้ช่วย');
    } finally {
      setRemovingHelper(null);
    }
  };

  if (!isOpen || !tripPlan) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              จัดการผู้ช่วย - {tripPlan.tripName}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>

          {helpers.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-400 text-4xl mb-4">👥</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                ยังไม่มีผู้ช่วย
              </h3>
              <p className="text-gray-600">
                เชิญเพื่อนมาเป็นผู้ช่วยจัดทริป
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h3 className="font-semibold text-purple-800 mb-3">
                  ผู้ช่วยที่ตอบรับแล้ว ({helpers.length} คน)
                </h3>
                {/* Trip Creator */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h3 className="font-semibold text-blue-800 mb-3">
                    ผู้สร้างทริป
                  </h3>
                  <div className="flex items-center bg-white p-3 rounded-lg border border-blue-200">
                    <span className="text-green-600 text-sm mr-2">✓</span>
                    <div className="flex-1">
                      <div className="flex items-center">
                        <HelperDisplayName helperEmail={tripPlan.userName || tripPlan.userId || ''} />
                        <span className="text-sm text-gray-500 ml-2">({tripPlan.userName || tripPlan.userId || 'ไม่ระบุ'})</span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <HelperGirlProfiles 
                        helperEmail={tripPlan.userName || tripPlan.userId || ''} 
                        tripPlanId={tripPlan.id} 
                      />
                    </div>
                  </div>
                </div>

                {/* Helpers */}
                <div className="space-y-3">
                  {helpers.map((helper, index) => (
                    <div key={index} className="flex items-center justify-between bg-white p-3 rounded-lg border border-purple-200">
                      <div className="flex items-center flex-1">
                        <span className="text-green-600 text-sm mr-2">✓</span>
                        <div className="flex-1">
                          <div className="flex items-center">
                            <HelperDisplayName helperEmail={helper.email} />
                            <span className="text-sm text-gray-500 ml-2">({helper.email})</span>
                          </div>
                          <p className="text-sm text-gray-500">
                            เข้าร่วมเมื่อ: {new Date(helper.joinedAt).toLocaleDateString('th-TH')}
                          </p>
                        </div>
                        <div className="ml-4">
                          <HelperGirlProfiles 
                            helperEmail={helper.email} 
                            tripPlanId={tripPlan.id} 
                          />
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveHelper(helper.email)}
                        disabled={removingHelper === helper.email}
                        className={`px-3 py-1 rounded text-sm ml-4 ${
                          removingHelper === helper.email
                            ? 'bg-gray-400 text-white cursor-not-allowed'
                            : 'bg-red-500 text-white hover:bg-red-600'
                        }`}
                      >
                        {removingHelper === helper.email ? 'กำลังลบ...' : 'ลบ'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h3 className="font-semibold text-yellow-800 mb-2">
                  สิทธิ์ของผู้ช่วย
                </h3>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li>✅ ช่วยทำ checklist ทริป</li>
                  <li>✅ จองคิวน้องแทนผู้จัดทริป</li>
                  <li>✅ ดูข้อมูลทริปทั้งหมด</li>
                  <li>❌ แก้ไขรายละเอียดทริป</li>
                  <li>❌ ลบทริป</li>
                </ul>
              </div>
            </div>
          )}

          <div className="flex justify-end mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition"
            >
              ปิด
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelperManagementModal;
